import { Injectable, Injector } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError, EMPTY, NEVER } from 'rxjs';

import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngxs/store';
import { Logout } from '../../store/user/user.actions';
import { Router } from '@angular/router';
import { UserState } from '../../store/user/user.store';
import app from 'package.json';
import {AuthToken} from "../models/auth-token.model";
import { AuthService } from '../services/auth.service';
import { GlobalLoaderService } from '../services/global-loader.service';

export const ASSISTED_AUTH_TOKEN = new HttpContextToken<AuthToken | undefined>(() => undefined);
export const BYPASS_403_ERROR = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRedirecting = false;

  constructor(private toastr: ToastrService, private store: Store, private router: Router, private injector: Injector) { }

  intercept(request: HttpRequest<never>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip adding headers for Google Maps API requests
    if (request.url.includes('maps.googleapis.com')) {
      return next.handle(request);
    }

    let authToken: AuthToken | undefined = undefined;
    // Assisted registration is using user token to perform requests on behalf of the user
    if (request.context.has(ASSISTED_AUTH_TOKEN) && request.context.get(ASSISTED_AUTH_TOKEN) !== undefined) {
      authToken = request.context.get(ASSISTED_AUTH_TOKEN);

    } else {
      authToken = this.store.selectSnapshot(UserState.authToken);
    }

    const appversion = app.version.replace(/\./g, '');

    // Initialize source Headers
    let setHeaders: any = { appversion, "Sportshub-Subdomain": location.host }
    let existingHeaders = request.headers.keys();
    for (let i = 0; i < existingHeaders.length; i++) {
      let headerName: string = existingHeaders[i];
      setHeaders[headerName] = request.headers.get(headerName);
    }

    if (authToken && authToken.token && !request.headers.has('authorization')) {
      setHeaders['authorization'] = `Bearer ${authToken.token}`;
    }

    request = request.clone({ setHeaders });

    return next.handle(request).pipe(
      catchError((error, caught) => {
        // We MUST catch Session Expired Error only in case of Authorized Requests
        if (authToken && authToken.token && error instanceof HttpErrorResponse && error.status === 401) {
          this.store.dispatch(Logout);
          const url = this.router.url;
          if (!this.isRedirecting) {
            this.isRedirecting = true;
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
              setTimeout(() => {
                this.router.navigateByUrl(url, { replaceUrl: true }).then(() => {
                  this.injector.get(AuthService).openSignInDialog$.next(true);
                  setTimeout(() => this.isRedirecting = false, 1000);
                });
              }, 50)
            );
          }
        }

        if (error instanceof HttpErrorResponse && error.status === 403) {
          if (request.context.get(BYPASS_403_ERROR)) {
            return throwError(error);
          }

          const isLoginAs = this.store.selectSnapshot(UserState.isLoginAs);
          if (isLoginAs) {
            this.injector.get(GlobalLoaderService).hide();
            const currentUrl = this.router.url.split('?')[0];
            if (currentUrl !== '/' && currentUrl !== '/dashboard') {
              if (!this.isRedirecting) {
                this.isRedirecting = true;
                this.router.navigateByUrl('/', { replaceUrl: true }).then(() => {
                  setTimeout(() => this.isRedirecting = false, 1000);
                });
              }
            }
            return NEVER;
          }

          // this.store.dispatch(Logout);
          this.injector.get(GlobalLoaderService).hide();
          if (!this.isRedirecting) {
            this.isRedirecting = true;
            this.router.navigateByUrl('/', { replaceUrl: true }).then(() => {
              this.injector.get(AuthService).openSignInDialog$.next(true);
              setTimeout(() => this.isRedirecting = false, 1000);
            });
          }
          return NEVER;
        }

        if (error instanceof HttpErrorResponse && error.error && error.error.errorCode === 'TOO_LOW_APP_VERSION') {
          this.toastr.warning('Please update your application. Current version is no longer supported');
        }

        return throwError(error);
      }),
    );
  }
}
